import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './SearchBar.module.css';

const PLACEHOLDER_PREFIX = 'Каким будет ваш следующий ';
const PLACEHOLDER_WORDS = [
  'торт?',
  'капкейк?',
  'чизкейк?',
  'рулет?',
  'тирамису?',
  'брауни?',
  'эклер?',
  'макарон?',
  'пряник?',
];

const TYPE_MS = 70;
const DELETE_MS = 45;
const HOLD_MS = 1800;
const GAP_MS = 400;

function useTypingPlaceholder(enabled) {
  const [text, setText] = useState(PLACEHOLDER_PREFIX);

  useEffect(() => {
    if (!enabled) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setText(`${PLACEHOLDER_PREFIX}${PLACEHOLDER_WORDS[0]}`);
      return undefined;
    }

    let wordIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timerId;

    const tick = () => {
      const word = PLACEHOLDER_WORDS[wordIndex];

      if (!deleting) {
        charIndex += 1;
        setText(`${PLACEHOLDER_PREFIX}${word.slice(0, charIndex)}`);

        if (charIndex === word.length) {
          deleting = true;
          timerId = window.setTimeout(tick, HOLD_MS);
          return;
        }

        timerId = window.setTimeout(tick, TYPE_MS);
        return;
      }

      charIndex -= 1;
      setText(`${PLACEHOLDER_PREFIX}${word.slice(0, charIndex)}`);

      if (charIndex === 0) {
        deleting = false;
        wordIndex = (wordIndex + 1) % PLACEHOLDER_WORDS.length;
        timerId = window.setTimeout(tick, GAP_MS);
        return;
      }

      timerId = window.setTimeout(tick, DELETE_MS);
    };

    timerId = window.setTimeout(tick, GAP_MS);
    return () => window.clearTimeout(timerId);
  }, [enabled]);

  return text;
}

export function SearchBar({ className = '' }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const placeholder = useTypingPlaceholder(!query && !focused);

  useEffect(() => {
    setQuery(searchParams.get('q') || '');
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed) {
      navigate(`/catalog?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const clearSearch = () => {
    setQuery('');
    if (!searchParams.has('q')) return;

    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('page');

    if (location.pathname.startsWith('/catalog')) {
      setSearchParams(next, { replace: true });
    } else {
      const qs = next.toString();
      navigate(qs ? `/catalog?${qs}` : '/catalog');
    }
  };

  return (
    <form className={`${styles.form} ${className}`} onSubmit={handleSearch}>
      <input
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label="Поиск товаров"
      />
      {query && (
        <button type="button" className={styles.clearBtn} onClick={clearSearch} aria-label="Очистить">
          ×
        </button>
      )}
      <button type="submit" className={styles.submitBtn} aria-label="Найти">
        <SearchIcon />
      </button>
    </form>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
